import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHARGE_TYPES = [
  { value: 'EXTRA', label: 'Extra Charge' },
  { value: 'FEE', label: 'Fee' },
  { value: 'SERVICE', label: 'Service' },
  { value: 'MINIBAR', label: 'Minibar' },
  { value: 'DAMAGE', label: 'Damage' },
];

const AddChargeModal = ({ open, onClose, reservation, onAdded }) => {
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [chargeType, setChargeType] = useState('EXTRA');
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const handleSubmit = async () => {
    if (!description.trim()) { toast.error('Enter a description'); return; }
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount <= 0) { toast.error('Enter a valid amount'); return; }

    setSaving(true);
    try {
      await axios.post(
        `${API}/reservations/${reservation.id}/add-charge`,
        { description: description.trim(), amount: parsedAmount, charge_type: chargeType },
        { headers }
      );
      toast.success(`$${parsedAmount.toFixed(2)} charge added`);
      setDescription('');
      setAmount('');
      setChargeType('EXTRA');
      onAdded?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add charge');
    } finally {
      setSaving(false);
    }
  };

  if (!open || !reservation) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="add-charge-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign size={18} />
            Add Charge
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-3 bg-slate-50 rounded-lg text-sm">
            <p className="font-medium">{reservation.guest?.name}</p>
            <p className="text-xs text-slate-500">
              {reservation.check_in} to {reservation.check_out}
            </p>
          </div>

          <div>
            <Label>Description</Label>
            <Input
              placeholder="e.g. Late checkout fee, Minibar, etc."
              value={description}
              onChange={e => setDescription(e.target.value)}
              data-testid="add-charge-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                data-testid="add-charge-amount"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={chargeType} onValueChange={setChargeType}>
                <SelectTrigger data-testid="add-charge-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHARGE_TYPES.map(ct => (
                    <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="add-charge-submit-btn">
              {saving ? 'Adding...' : 'Add Charge'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddChargeModal;
